import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { SectionHeader, Reveal } from "../../../components/common";
import { teamMembers } from "../../../config/constants";

/* One-line studio superlatives, keyed by member id. */
const TAGLINES = {
  1: "Turns blank pages into brand stories.",
  2: "Turns wild ideas into working software.",
  3: "Turns quiet brands into loud ones.",
};

/*
 * Staggered "air" clocks so the three badges never swing in sync:
 * duration, negative delay (mid-swing on load) and a slight resting lean.
 */
const AIR = [
  { dur: "7.6s", delay: "-1.2s", base: "-1.4deg" },
  { dur: "8.4s", delay: "-4.1s", base: "0.9deg" },
  { dur: "9.2s", delay: "-2.6s", base: "1.6deg" },
];

/* Per-photo focal points for the badge crop, keyed by member id. */
const PHOTO_FOCUS = {
  1: "center 8%",
};

const MAX_TILT = 8;

/**
 * Pointer-driven 3D tilt with lerped smoothing. Writes unitless numbers to
 * --rx/--ry (turned into degrees in CSS) plus glare position/opacity.
 * Fine pointers only, and always off under prefers-reduced-motion.
 */
const useBadgeTilt = (ref) => {
  const frame = useRef(0);
  const target = useRef({ rx: 0, ry: 0, gx: 50, gy: 30, glare: 0 });
  const current = useRef({ rx: 0, ry: 0, gx: 50, gy: 30, glare: 0 });

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  const tiltAllowed = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: fine)").matches &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const tick = () => {
    const el = ref.current;
    const t = target.current;
    const c = current.current;
    let settled = true;
    ["rx", "ry", "gx", "gy", "glare"].forEach((k) => {
      c[k] += (t[k] - c[k]) * 0.14;
      if (Math.abs(t[k] - c[k]) > 0.02) settled = false;
    });
    if (el) {
      el.style.setProperty("--rx", c.rx.toFixed(2));
      el.style.setProperty("--ry", c.ry.toFixed(2));
      el.style.setProperty("--gx", `${c.gx.toFixed(1)}%`);
      el.style.setProperty("--gy", `${c.gy.toFixed(1)}%`);
      el.style.setProperty("--glare", c.glare.toFixed(3));
    }
    frame.current = settled ? 0 : requestAnimationFrame(tick);
  };

  const kick = () => {
    if (!frame.current) frame.current = requestAnimationFrame(tick);
  };

  const onPointerMove = (e) => {
    if (!tiltAllowed() || !ref.current) return;
    // Over the text zone (name, roles, links): ease back to flat so links
    // hold still under the cursor instead of dodging it.
    if (e.target.closest?.(".id-body")) {
      target.current = { rx: 0, ry: 0, gx: 50, gy: 30, glare: 0.12 };
      kick();
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    target.current = {
      rx: -py * 2 * MAX_TILT,
      ry: px * 2 * MAX_TILT,
      gx: (px + 0.5) * 100,
      gy: (py + 0.5) * 100,
      glare: 0.55,
    };
    kick();
  };

  const onPointerLeave = () => {
    target.current = { rx: 0, ry: 0, gx: 50, gy: 30, glare: 0 };
    kick();
  };

  return { onPointerMove, onPointerLeave };
};

const AffiliationLine = ({ aff }) => {
  if (!aff.org)
    return (
      <li>
        <span className="id-role">{aff.role}</span>
      </li>
    );
  const isInternal = aff.org.url && !aff.org.url.startsWith("http");
  if (isInternal) {
    return (
      <li>
        <span className="id-role">{aff.role}</span>
        {", "}
        <Link to={aff.org.url}>{aff.org.name}</Link>
      </li>
    );
  }
  return (
    <li>
      <span className="id-role">{aff.role}</span>
      {", "}
      <a
        href={aff.org.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${aff.org.name}, opens in a new tab`}
      >
        {aff.org.name}
      </a>
    </li>
  );
};

const IdBadge = ({ member, motion }) => {
  const cardRef = useRef(null);
  const { onPointerMove, onPointerLeave } = useBadgeTilt(cardRef);
  const tagline = TAGLINES[member.id] || "Makes good things for good people.";

  const photo = member.image ? (
    <img
      src={member.image}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      className="id-photo"
      style={PHOTO_FOCUS[member.id] ? { objectPosition: PHOTO_FOCUS[member.id] } : undefined}
    />
  ) : (
    <div className="id-photo-fallback" aria-hidden="true">
      {member.initials}
    </div>
  );

  return (
    <div className="id-scene">
      <div
        className="id-sway"
        style={{
          "--sway-dur": motion.dur,
          "--sway-delay": motion.delay,
          "--base-rot": motion.base,
        }}
      >
        {/*
          Strap + card bob as one rigid piece so the clip never detaches
          from the badge mid-swing.
        */}
        <div className="id-bob">
        {/* Lanyard strap + clip the badge hangs from */}
        <div className="id-strap" aria-hidden="true">
          <div className="id-strap-band" />
          <div className="id-clip">
            <span className="id-ring" />
            <span className="id-jaw" />
          </div>
        </div>

        <div className="id-float">
          <article
            ref={cardRef}
            onPointerMove={onPointerMove}
            onPointerLeave={onPointerLeave}
            className="id-card"
            aria-label={`${member.name} studio pass`}
          >
            <div className="id-head">
              <span className="id-brand">
                <span className="tick" aria-hidden="true" />
                Arvix Creative
              </span>
              <span className="id-pass">Studio pass</span>
            </div>

            {member.link ? (
              <a
                href={member.link}
                target="_blank"
                rel="noopener noreferrer"
                className="id-photo-link"
                tabIndex={-1}
                aria-hidden="true"
              >
                {photo}
              </a>
            ) : (
              photo
            )}

            <div className="id-body">
              <p className="id-tagline">{tagline}</p>
              <h3 className="id-name">
                {member.link ? (
                  <a
                    href={member.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${member.name}'s portfolio, opens in a new tab`}
                  >
                    {member.name}
                  </a>
                ) : (
                  member.name
                )}
              </h3>

              <ul className="id-roles">
                {member.affiliations.map((aff, idx) => (
                  <AffiliationLine key={idx} aff={aff} />
                ))}
              </ul>

              <div className="id-foot">
                <span className="id-meta" aria-hidden="true">
                  <span className="id-code" />
                  <span className="id-no">
                    NO. AC-00{member.id}
                  </span>
                </span>
              </div>
            </div>

            <div className="id-glare" aria-hidden="true" />
          </article>
        </div>
        </div>
      </div>

      <div
        className="id-ground"
        aria-hidden="true"
        style={{ "--sway-dur": motion.dur, "--sway-delay": motion.delay }}
      />
    </div>
  );
};

const TeamSection = () => {
  return (
    <section id="team-section" className="relative py-16 md:py-24">
      <SectionHeader
        index="04"
        eyebrow="The crew"
        title={<>Meet the minds behind the pixels.</>}
        lede="A designer, an engineer and a marketer walk into a studio. Twelve dozen projects later, they are still answering every message like partners, not vendors."
        layout="split"
      />

      {/*
        Studio passes on lanyards: equal badges, no hierarchy. Idle sway +
        float runs on each badge's own clock; pointer tilt is layered on the
        card only, so the two motions never fight over one transform.
      */}
      <div className="relative mx-auto mt-6 max-w-5xl md:mt-8">
        <div
          className="texture-dots pointer-events-none absolute inset-0 opacity-70 [mask-image:radial-gradient(65%_60%_at_50%_35%,black,transparent)]"
          aria-hidden="true"
        />
        <div className="relative grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {teamMembers.map((member, i) => (
            <Reveal
              key={member.id}
              delay={(i % 3) * 110}
              className={`relative z-10 h-full${
                /* Center the odd badge out on two-column screens */
                i === teamMembers.length - 1 && teamMembers.length % 2 === 1
                  ? " sm:col-span-2 sm:mx-auto sm:w-full sm:max-w-xs lg:col-span-1 lg:max-w-none"
                  : ""
              }`}
            >
              <IdBadge member={member} motion={AIR[i % AIR.length]} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
